import {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {KTIcon} from './_mesomb/helpers';
import {requestWorkerActivation, safelyInstallUpdate, updateBlockReason, validVersionPolicy, waitForActivation, waitForWaitingWorker} from './app/pwa-update.mjs';
import './PWABadge.css';

const UPDATE_INTERVAL = 15 * 60 * 1000;
const UPDATE_RETRY_INTERVAL = 60 * 1000;
const VERSION_ENDPOINT = import.meta.env.VITE_APP_VERSION_ENDPOINT || '/version.json';
interface VersionPolicy {latest_version:string; minimum_supported_version:string; force_update?:boolean; message?:string;}
const compareVersions = (left:string, right:string) => {
  const a=left.split('.').map(Number), b=right.split('.').map(Number);
  for (let i=0;i<3;i++) if(a[i]!==b[i]) return a[i]>b[i]?1:-1;
  return 0;
};
const explanations = {
  offline:'Reconnect before installing an update.',
  operation:'Finish the current operation and check its status before updating. An update will not restart your payment.',
  payment:'Finish this payment journey before updating. You can update from the home page afterwards.',
  tabs:'Close the other MeSomb tabs before updating. Their ongoing operations must finish first.',
  failed:'The update could not be installed. Your page has not been reloaded. Please try again later.',
};

export default function PWABadge() {
  const {t}=useTranslation();
  const registrationRef=useRef<ServiceWorkerRegistration>();
  const mounted=useRef(true);
  const [policy,setPolicy]=useState<VersionPolicy>();
  const [needRefresh,setNeedRefresh]=useState(false);
  const [updating,setUpdating]=useState(false);
  const [blocked,setBlocked]=useState<ReturnType<typeof updateBlockReason>>(updateBlockReason());
  const [error,setError]=useState('');
  const updateRequired=Boolean(policy && (compareVersions(__APP_VERSION__,policy.minimum_supported_version)<0 ||
    policy.force_update && compareVersions(__APP_VERSION__,policy.latest_version)<0));
  const serverVersionAvailable=Boolean(policy && compareVersions(policy.latest_version,__APP_VERSION__)>0);

  const checkForUpdate=useCallback(async () => {
    if(!navigator.onLine || document.visibilityState==='hidden') return;
    try {
      const response=await fetch(`${VERSION_ENDPOINT}${VERSION_ENDPOINT.includes('?')?'&':'?'}time=${Date.now()}`,{
        cache:'no-store',credentials:'omit',referrerPolicy:'no-referrer',signal:AbortSignal.timeout(10000),
      });
      if(response.ok) {const value:unknown=await response.json();if(mounted.current && validVersionPolicy(value)) setPolicy(value as VersionPolicy);}
    } catch { /* An unavailable or invalid policy never forces a reload. */ }
    try {await registrationRef.current?.update();} catch { /* Retry later. */ }
  },[]);

  useEffect(() => {
    mounted.current=true;
    const disposers:Array<()=>void>=[];
    if(import.meta.env.PROD && 'serviceWorker' in navigator) {
      void navigator.serviceWorker.register('/sw.js',{scope:'/',updateViaCache:'none'}).then(registration => {
        if(!mounted.current) return;
        registrationRef.current=registration;
        const track=() => {
          if(registration.waiting) setNeedRefresh(true);
          const worker=registration.installing;
          if(worker) {
            const changed=() => {
              // Some browsers expose registration.waiting just after the worker's
              // statechange event; retry in the next microtask before giving up.
              queueMicrotask(()=>{if(mounted.current && registration.waiting) setNeedRefresh(true);});
            };
            worker.addEventListener('statechange',changed);
            disposers.push(()=>worker.removeEventListener('statechange',changed));
          }
        };
        registration.addEventListener('updatefound',track);
        disposers.push(()=>registration.removeEventListener('updatefound',track));
        track();void checkForUpdate();
      }).catch(()=>{ /* Registration failure keeps the online application usable. */ });
    }
    const checkSafety=()=>setBlocked(updateBlockReason());
    const safetyInterval=window.setInterval(checkSafety,1000);
    const interval=window.setInterval(checkForUpdate,UPDATE_INTERVAL);
    void checkForUpdate();
    window.addEventListener('online',checkForUpdate);window.addEventListener('focus',checkForUpdate);
    window.addEventListener('mesomb:update-safety',checkSafety);window.addEventListener('offline',checkSafety);
    document.addEventListener('visibilitychange',checkForUpdate);
    return () => {
      mounted.current=false;disposers.forEach(dispose=>dispose());
      window.clearInterval(interval);window.clearInterval(safetyInterval);
      window.removeEventListener('online',checkForUpdate);window.removeEventListener('focus',checkForUpdate);
      window.removeEventListener('mesomb:update-safety',checkSafety);window.removeEventListener('offline',checkSafety);
      document.removeEventListener('visibilitychange',checkForUpdate);
    };
  },[checkForUpdate]);

  // A deployed version policy can arrive shortly before its service-worker
  // assets reach every CDN edge. Keep checking quietly; only surface an update
  // after the browser has a worker that can actually activate it.
  useEffect(() => {
    if(!serverVersionAvailable || needRefresh) return;
    const retry=window.setInterval(checkForUpdate,UPDATE_RETRY_INTERVAL);
    return ()=>window.clearInterval(retry);
  },[checkForUpdate,needRefresh,serverVersionAvailable]);

  const installUpdate=async () => {
    if(updating) return;
    setUpdating(true);setError('');
    try {
      await safelyInstallUpdate(async () => {
        const registration=registrationRef.current;
        if(!registration) throw new Error('failed');
        await registration.update();
        const worker=await waitForWaitingWorker(registration);
        await requestWorkerActivation(worker);
        await waitForActivation(worker);
        window.location.reload();
      });
    } catch(reason) {
      const code=reason instanceof Error?reason.message:'';
      if(mounted.current) {
        // The worker may have been replaced or discarded while the user was
        // clicking. Return to background detection instead of offering an
        // action that cannot succeed.
        if(code==='failed' && !updateRequired) {setNeedRefresh(false);void checkForUpdate();}
        else setError(t(explanations[code as keyof typeof explanations] || explanations.failed));
      }
    } finally {if(mounted.current) setUpdating(false);}
  };

  if(navigator.onLine === false) return null;
  // Never cover a payment or an unresolved operation with a mandatory-update modal.
  if(updateRequired && !blocked) return <div className='pwa-update-required' role='alertdialog' aria-modal='true' aria-labelledby='required-update-title'>
    <div className='card border-0 pwa-update-required__card'><div className='card-body p-7 text-center'>
      <h1 id='required-update-title' className='fs-2'>{t('Update MeSomb to continue')}</h1>
      <p>{policy?.message || t('This version is no longer supported. Update now to keep your account secure and continue using MeSomb.')}</p>
      <p>{t('Current version')}: {__APP_VERSION__} · {t('Required version')}: {policy?.minimum_supported_version}</p>
      {error && <p role='alert'>{error}</p>}
      <button className='btn btn-primary' type='button' disabled={updating} onClick={installUpdate}>{t(updating?'Updating...':'Update now')}</button>
    </div></div>
  </div>;
  if(!needRefresh && !updateRequired) return null;
  const updateToast = <aside className='pwa-update card border-0' role='status' aria-live='polite' aria-labelledby='pwa-update-title'>
    <div className='card-body p-4'>
      <div id='pwa-update-title' className='fw-bold'>{t(updateRequired?'Required update':'Update available')}</div>
      <p>{blocked?t(explanations[blocked]):t('A new version is ready. Refresh now to keep using the latest improvements.')}</p>
      {error && <p role='alert'>{error}</p>}
      <button className='btn btn-sm btn-primary' type='button' disabled={updating || Boolean(blocked)} onClick={installUpdate}>
        <KTIcon iconName='arrows-circle' className='fs-4 me-1'/>{t(updating?'Updating...':'Refresh now')}
      </button>
      {!updateRequired && <button className='btn btn-sm btn-light ms-2' type='button' onClick={()=>setNeedRefresh(false)}>{t('Later')}</button>}
    </div>
  </aside>;
  // A portal prevents a transformed or clipped app layout from changing the
  // fixed toast's viewport position on dashboard, pay, and my entry points.
  return createPortal(updateToast, document.body);
}
